import { useEffect } from "react";
import { APP_CONFIG } from "../constants";
import { firebaseConfig } from "../firebaseConfig";
import axios from "axios";
import { isTransientNetworkError } from "../utils/clientEnvironment";

const NOTIFICATION_PROMPT_KEY = "ch_notification_prompt_last_at";
const NOTIFICATION_PROMPT_COOLDOWN_MS = 24 * 60 * 60 * 1000;

const isMobileClient = () => {
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera || "";
    return /android|iphone|ipad|ipod/i.test(ua) || window.matchMedia("(max-width: 900px)").matches;
};

const registerFcmAndListener = async () => {
    if (Notification.permission !== "granted") return;
    const vapidKey = APP_CONFIG.NOTIFICATIONS.FIREBASE_VAPID_KEY;
    if (!vapidKey) {
        return;
    }

    const [appModule, messagingModule] = await Promise.all([
        import("firebase/app"),
        import("firebase/messaging"),
    ]);
    if (!(await messagingModule.isSupported())) return;
    const app = appModule.getApps().length > 0
        ? appModule.getApp()
        : appModule.initializeApp(firebaseConfig);
    const messaging = messagingModule.getMessaging(app);

    const swRegistration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");

    const token = await messagingModule.getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: swRegistration,
    });

    if (token) {
        let deviceId = localStorage.getItem("ch_device_id");
        if (!deviceId) {
            deviceId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
            localStorage.setItem("ch_device_id", deviceId);
        }

        const authToken = localStorage.getItem(APP_CONFIG.STORAGE_KEYS.TOKEN);
        if (authToken) {
            await axios.post(
                `${APP_CONFIG.API_URL}/api/Notifications/save-token`,
                { token, deviceType: `Web|${deviceId}` },
                { headers: { Authorization: `Bearer ${authToken}` } }
            );
        }
    }

    messagingModule.onMessage(messaging, (payload) => {
        console.log("Foreground push alindi:", payload);
    });
};

const syncAppVisibility = async () => {
    const authToken = localStorage.getItem(APP_CONFIG.STORAGE_KEYS.TOKEN);
    if (!authToken) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;

    const isForeground = typeof document !== "undefined"
        ? document.visibilityState === "visible"
        : true;

    await axios.post(
        `${APP_CONFIG.API_URL}/api/Notifications/app-visibility`,
        { isForeground },
        { headers: { Authorization: `Bearer ${authToken}` } }
    );
};

export const useNotifications = () => {
    useEffect(() => {
        let disposed = false;
        let inFlight = false;

        const setupNotifications = async () => {
            if (disposed || inFlight) return;
            inFlight = true;
            try {
                if (!("Notification" in window)) return;

                if (isMobileClient() && Notification.permission === "default") {
                    const now = Date.now();
                    const lastPromptAt = Number(localStorage.getItem(NOTIFICATION_PROMPT_KEY) || "0");
                    if (now - lastPromptAt > NOTIFICATION_PROMPT_COOLDOWN_MS) {
                        localStorage.setItem(NOTIFICATION_PROMPT_KEY, String(now));
                        await Notification.requestPermission();
                    }
                }

                await registerFcmAndListener();
                await syncAppVisibility();
            } catch (error) {
                console.error("FCM setup error:", error);
            } finally {
                inFlight = false;
            }
        };

        const handleAuthStateChanged = () => {
            void setupNotifications();
        };

        const handleVisibilityChanged = () => {
            void syncAppVisibility().catch((error) => {
                if (!isTransientNetworkError(error)) {
                    console.error("App visibility sync error:", error);
                }
            });
            if (document.visibilityState === "visible") {
                void setupNotifications();
            }
        };

        const handleFocus = () => {
            void setupNotifications();
        };

        void setupNotifications();
        window.addEventListener("auth-state-changed", handleAuthStateChanged);
        document.addEventListener("visibilitychange", handleVisibilityChanged);
        window.addEventListener("focus", handleFocus);

        return () => {
            disposed = true;
            window.removeEventListener("auth-state-changed", handleAuthStateChanged);
            document.removeEventListener("visibilitychange", handleVisibilityChanged);
            window.removeEventListener("focus", handleFocus);
        };
    }, []);
};
